<script setup>
/**
 * The only multi-line text control in the Waslo tree. THE PLAN, §5 R7.
 *
 * WHY IT EXISTS AT ALL, given `components-next/textarea/TextArea.vue` is right there:
 * `dir="auto"` has to sit on the `<textarea>` element itself. Direction inherited from a
 * wrapper is the wrapper's RESOLVED direction, not per-value auto-detection — so an
 * Arabic handover message inside an English dashboard renders with the wrong base
 * direction, which is exactly the defect visible in `shots/ours/persona-light.png`.
 * Chatwoot's TextArea does not forward attributes to the inner element, so there is no
 * prop, class or wrapper that can fix it from outside. THE PLAN, §6 R-9.
 *
 * Everything else here is Chatwoot's TextArea, class for class: same `bg-n-alpha-black2`
 * fill, same `rounded-lg`, same border states, same `text-sm`, same character counter.
 * The `dark:` duplicates are dropped rather than copied — every token in them already
 * resolves per theme, which is the point of the contract (§5 R4).
 *
 * `resize-none` + auto-height is deliberate: a prompt box that grows with its content
 * removes the scroll-inside-a-scroll that made the old textarea unusable on a phone.
 *
 * ── WHAT A BLIND CRITIC SAW HERE, AND WHAT CHANGED ────────────────────────────────────
 * Two independent critics, light theme and dark, reached the same verdict unprompted:
 * "all four textareas are borderless grey fills; only the focused one has an edge, so the
 * other three read as a read-only summary rather than boxes you can type in."
 *
 *   • RESTING EDGE. `border-n-weak` is 234,234,234 on a 254,254,254 page and 31,31,37 on a
 *     20,21,23 one — a one-to-two-step difference that survives a colour picker and not a
 *     glance. It is the right token for a divider and the wrong one for the only thing on
 *     the page telling you a control is a control. The rest state is now `n-slate-7`
 *     (205,206,214 light / 67,72,78 dark) — Radix step 7, "UI element border", which is
 *     what a form control at rest is. Step 6 was tried first and measured: 217 on a 254
 *     page is a real edge, but only just, and "only just" is what got us here.
 *   • THE HINT MOVED ABOVE THE FIELD, at `text-sm`, not `text-xs` under it. Under the
 *     field it shared a baseline with the assist link, sat below the next label's optical
 *     start, and bound to nothing. Above it, it is read before the box it explains.
 *   • The gaps inside this component are all ≤ 8px, and the caller separates fields by
 *     32px, so label + hint + field + message read as one object and four of them read as
 *     four questions — the "wall of grey paragraphs" the dark critic described.
 *
 * The `labelAction` slot exists for the same reason: an affordance that belongs to the
 * field (the assist link) sits on the label row, where it cannot crowd the hint.
 */
import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { formatCount } from '../helpers/plural.js';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  /**
   * What to write in the box, ABOVE the box. Read before you type, not after — and never
   * on the same line as anything clickable.
   */
  hint: { type: String, default: '' },
  /** Result of an action — validation, mostly. Sits under the field, where you look after
   * you pressed something. Coloured by `messageType`. */
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  disabled: { type: Boolean, default: false },
  autofocus: { type: Boolean, default: false },
  minHeight: { type: String, default: '5rem' },
  maxHeight: { type: String, default: '22rem' },
  maxLength: { type: Number, default: 0 },
  /**
   * Whether the `{count} / {max}` readout is drawn. Default TRUE, because every existing
   * caller has a limit that is a real constraint — a WhatsApp card body, an option label —
   * where the number is the whole point and the counters were hard-won.
   *
   * It is `false` on exactly one kind of box: one whose `maxLength` is a backstop rather
   * than a budget. A blind reader hit that case on the "when should Waslo start it?" box
   * and read `59 / 500` as "12% done, keep writing" — the counter had turned one honest
   * sentence into a form he was failing to fill. The limit still holds; only the scoreboard
   * for a race nobody is running goes away.
   */
  showCount: { type: Boolean, default: true },
  readonly: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur']);

const fieldId = `waslo-textarea-${getCurrentInstance().uid}`;
const fieldRef = ref(null);
const isFocused = ref(false);

const messageClass = computed(() => {
  if (props.messageType === 'error') return 'text-n-ruby-9';
  if (props.messageType === 'success') return 'text-n-teal-10';
  return 'text-n-slate-11';
});

const borderClass = computed(() => {
  if (props.disabled) return 'border-n-slate-5 opacity-50 cursor-not-allowed';
  // `message` has to be non-empty, not just `messageType === 'error'`. A caller that
  // declares "when this field has something to say, it is an error" and has nothing to
  // say yet is the ordinary resting case — reading the type alone painted three untouched
  // fields ruby on first paint.
  if (props.messageType === 'error' && props.message && !isFocused.value) {
    return 'border-n-ruby-8 hover:border-n-ruby-9';
  }
  if (isFocused.value) return 'border-n-brand';
  // The resting edge. See the header: `n-weak` is a divider token and made every
  // unfocused field read as a paragraph rather than a control.
  return 'border-n-slate-7 hover:border-n-slate-8';
});

const characterCount = computed(() => (props.modelValue ?? '').length);

// "17 / 200" in the screen's digits (O-5): Arabic-Indic on an Arabic screen. A readout, so
// no grouping, exactly as it read before.
const { locale } = useI18n();
const countLabel = computed(
  () =>
    `${formatCount(characterCount.value, locale.value, { useGrouping: false })} / ${formatCount(props.maxLength, locale.value, { useGrouping: false })}`
);

// `maxlength` stops the 1,025th character being TYPED, so this can only ever fire for a value
// that arrived from somewhere else — a template written in WhatsApp Manager and read back for
// editing. It is the same treatment `MenuPreview` gives an over-length option title, and for
// the same reason: the count is the fact, and the colour is what makes it get read.
const countClass = computed(() =>
  props.maxLength && characterCount.value > props.maxLength
    ? 'text-n-ruby-9'
    : 'text-n-slate-10'
);

// Height follows content, so the field never scrolls inside the page's scroll.
const fitHeight = () => {
  const el = fieldRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const onInput = event => {
  emit('update:modelValue', event.target.value);
  nextTick(fitHeight);
};

watch(
  () => props.modelValue,
  () => nextTick(fitHeight)
);

onMounted(() => {
  nextTick(fitHeight);
  if (props.autofocus) fieldRef.value?.focus();
});

defineExpose({ focus: () => fieldRef.value?.focus() });
</script>

<template>
  <!-- One field = one object. Every gap in here is 4-8px; the caller separates fields by
       32px. That 4:1 ratio is what binds label + hint + box + message together. -->
  <div class="relative flex flex-col min-w-0">
    <div
      v-if="label || $slots.labelAction"
      class="flex items-center justify-between min-w-0 gap-4 mb-1"
    >
      <label :for="fieldId" class="text-sm font-medium text-n-slate-12">
        {{ label }}
      </label>
      <slot name="labelAction" />
    </div>
    <p v-if="hint" class="min-w-0 mb-2 text-sm text-n-slate-11">
      {{ hint }}
    </p>
    <div
      class="flex flex-col gap-2 px-3 py-3 transition-all duration-300 ease-in-out border rounded-lg bg-n-alpha-black2"
      :class="borderClass"
    >
      <!-- The one sanctioned raw textarea. `waslo-lint` exempts this file by name; see R7. -->
      <textarea
        :id="fieldId"
        ref="fieldRef"
        dir="auto"
        rows="1"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxLength || undefined"
        :style="{ minHeight, maxHeight }"
        class="flex w-full p-0 text-sm leading-6 resize-none reset-base !rounded-none !bg-transparent !border-0 !outline-0 !mb-0 text-n-slate-12 placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50"
        @input="onInput"
        @focus="
          isFocused = true;
          emit('focus', $event);
        "
        @blur="
          isFocused = false;
          emit('blur', $event);
        "
      />
      <div
        v-if="maxLength && showCount"
        class="flex items-center justify-end h-4"
      >
        <span
          class="text-xs tabular-nums"
          :class="countClass"
          data-waslo-textarea-count
        >
          {{ countLabel }}
        </span>
      </div>
    </div>
    <p v-if="message" class="min-w-0 mt-2 mb-0 text-sm" :class="messageClass">
      {{ message }}
    </p>
  </div>
</template>
